import { useState, useEffect } from 'react';
import { MapPin, Plus, Edit, Trash2, Check, X, Search, Navigation, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

export default function AdminDeliveryAddresses() {
  const { token } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: 'Midrand',
    postal_code: '',
    province: 'Gauteng',
    latitude: '',
    longitude: '',
    delivery_fee: 0,
    is_default: false
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/delivery-addresses`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAddresses(data);
      }
    } catch (error) {
      toast.error('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const endpoint = editingAddress 
      ? `${API_URL}/api/admin/delivery-addresses/${editingAddress.address_id}`
      : `${API_URL}/api/admin/delivery-addresses`;
    
    const method = editingAddress ? 'PUT' : 'POST';
    
    // Clean up form data
    const payload = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      delivery_fee: parseFloat(formData.delivery_fee) || 0
    };

    try {
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingAddress ? 'Address updated' : 'Address created');
        setShowModal(false);
        resetForm();
        fetchAddresses();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save address');
      }
    } catch (error) {
      toast.error('Failed to save address');
    }
  };

  const handleDelete = async (addressId) => {
    if (!confirm('Are you sure you want to delete this address?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/delivery-addresses/${addressId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Address deleted');
        fetchAddresses();
      } else {
        toast.error('Failed to delete address');
      }
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      name: address.name || '',
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || 'Midrand',
      postal_code: address.postal_code || '',
      province: address.province || 'Gauteng',
      latitude: address.latitude || '',
      longitude: address.longitude || '',
      delivery_fee: address.delivery_fee || 0,
      is_default: address.is_default || false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingAddress(null);
    setFormData({
      name: '',
      address_line1: '',
      address_line2: '',
      city: 'Midrand',
      postal_code: '',
      province: 'Gauteng',
      latitude: '',
      longitude: '',
      delivery_fee: 0,
      is_default: false
    });
  };

  const toggleActive = async (address) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/delivery-addresses/${address.address_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !address.is_active })
      });

      if (response.ok) {
        toast.success(`Address ${!address.is_active ? 'activated' : 'deactivated'}`);
        fetchAddresses();
      }
    } catch (error) {
      toast.error('Failed to update address');
    }
  };

  const filteredAddresses = addresses.filter(addr =>
    addr.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    addr.address_line1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    addr.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="admin-delivery-addresses">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-foreground flex items-center gap-3">
            <MapPin className="h-8 w-8 text-primary" />
            Delivery Addresses
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage pre-loaded delivery locations for customers
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="gap-2"
          data-testid="add-address-btn"
        >
          <Plus className="h-4 w-4" />
          Add Address
        </Button>
      </div>

      {/* Info Card */}
      <Card className="mb-6 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Navigation className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Base Location: 19 Richards Drive, Midrand
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Delivery distances are calculated from this location. Customers can also enter custom addresses within 80km.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search addresses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Addresses Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading addresses...</div>
      ) : filteredAddresses.length === 0 ? (
        <Card className="bg-muted/50 border-dashed">
          <CardContent className="p-12 text-center">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">No Addresses Found</h3>
            <p className="text-muted-foreground/80 mb-4">Add delivery addresses for customers to select from.</p>
            <Button onClick={() => setShowModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Address
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredAddresses.map((address) => (
            <Card 
              key={address.address_id}
              className={`transition-all ${!address.is_active ? 'opacity-60' : ''}`}
              data-testid={`address-card-${address.address_id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                      {address.name}
                      {address.is_default && (
                        <Badge className="bg-primary/20 text-primary text-xs">Default</Badge>
                      )}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={address.is_active}
                      onCheckedChange={() => toggleActive(address)}
                      data-testid={`toggle-active-${address.address_id}`}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    {address.address_line1}
                    {address.address_line2 && <><br />{address.address_line2}</>}
                  </p>
                  <p className="text-muted-foreground">
                    {address.city}, {address.province} {address.postal_code}
                  </p>
                  
                  {/* Distance & Fee */}
                  <div className="flex items-center gap-4 pt-2 border-t border-border mt-3">
                    {address.distance_km && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Navigation className="h-3 w-3" />
                        {address.distance_km.toFixed(1)} km
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-xs">
                      <DollarSign className="h-3 w-3 text-green-600" />
                      <span className="font-medium text-green-600">
                        {address.delivery_fee > 0 ? `R${address.delivery_fee.toFixed(2)}` : 'Free'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(address)}
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(address.address_id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              {editingAddress ? 'Edit Address' : 'Add Delivery Address'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Location Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Gallagher Convention Centre"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="address_line1">Address Line 1 *</Label>
              <Input
                id="address_line1"
                placeholder="Street address"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="address_line2">Address Line 2</Label>
              <Input
                id="address_line2"
                placeholder="Building, floor, etc."
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  placeholder="-25.9892"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  placeholder="28.1275"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Coordinates help calculate distance. Get from Google Maps.
            </p>

            <div>
              <Label htmlFor="delivery_fee">Fixed Delivery Fee (R)</Label>
              <Input
                id="delivery_fee"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.delivery_fee}
                onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Set 0 for free delivery to this location
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_default"
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
              />
              <Label htmlFor="is_default" className="cursor-pointer">
                Set as default address
              </Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                {editingAddress ? 'Update Address' : 'Create Address'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
